import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { FileText, Download, ShieldCheck, UserCheck, Briefcase, Eye, Info } from 'lucide-react';
import Translate from '../components/Translate';
import { db } from '../firebase';
import { collection, onSnapshot } from 'firebase/firestore';

const Downloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'downloads'), (snapshot) => {
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDownloads(docs);
      setLoading(false);
    });
    return unsub;
  }, []);

  const categories = [
    {
      title: 'Farmer Registration & Forms',
      icon: UserCheck,
      color: 'bg-emerald/10 text-emerald'
    },
    {
      title: 'Corporate Reports & Policies',
      icon: Briefcase,
      color: 'bg-gold/10 text-gold'
    },
    {
      title: 'Certifications & Compliance',
      icon: ShieldCheck,
      color: 'bg-sage/15 text-emerald'
    }
  ];

  const viewPdf = (base64Data) => {
    if (!base64Data) return;
    const newTab = window.open();
    if (newTab) {
      newTab.document.write(`<iframe src="${base64Data}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    } else {
      alert("Please allow popups to view this PDF.");
    }
  };

  return (
    <div className="min-h-screen bg-cream pb-20 font-body selection:bg-emerald selection:text-white">
      <PageHeader 
        title={<Translate>Download Center</Translate>} 
        subtitle={<Translate>Important Resources & Documents</Translate>} 
      />

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          {loading ? (
            <div className="text-center py-20 text-slate">
              <Translate>Loading available documents...</Translate>
            </div>
          ) : (
            <div className="space-y-12">
              {categories.map((category, catIdx) => {
                const IconComponent = category.icon;
                const categoryItems = downloads.filter(item => item.category === category.title);

                return (
                  <div key={catIdx} className="premium-card p-8 bg-white relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gray-50 rounded-bl-full pointer-events-none -z-10 group-hover:scale-105 transition-transform duration-500"></div>

                    <div className="flex items-center space-x-4 mb-8">
                      <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center shadow-sm`}>
                        <IconComponent size={22} />
                      </div>
                      <div>
                        <h3 className="text-xl font-heading font-extrabold text-charcoal">
                          <Translate>{category.title}</Translate>
                        </h3>
                        <p className="text-[10px] text-slate/40 uppercase font-heading font-bold tracking-wider mt-0.5">
                          <Translate>Available Publications</Translate>
                        </p>
                      </div>
                    </div>

                    {categoryItems.length === 0 ? (
                      <div className="text-slate text-sm p-4 bg-ivory/50 rounded-xl flex items-center gap-2">
                        <Info size={16} />
                        <span><Translate>No files in this section currently.</Translate></span>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {categoryItems.map((item, itemIdx) => (
                          <div 
                            key={itemIdx} 
                            className="p-5 border border-gray-150 rounded-xl hover:border-emerald/20 hover:shadow-premium transition-all duration-300 flex items-center justify-between bg-ivory/30 group/item"
                          >
                            <div className="flex items-center space-x-4 max-w-[70%]">
                              <div className="w-10 h-10 bg-white rounded-lg border border-gray-150 flex items-center justify-center text-slate/40 group-hover/item:text-emerald transition-colors flex-shrink-0 shadow-sm">
                                <FileText size={18} />
                              </div>
                              <div>
                                <h4 className="text-sm font-heading font-bold text-charcoal leading-tight line-clamp-1 group-hover/item:text-emerald transition-colors" title={item.title}>
                                  <Translate>{item.title}</Translate>
                                </h4>
                                <p className="text-[10px] text-slate/50 font-body font-semibold mt-1">
                                  {item.fileType || 'PDF'} • {item.fileSize || 'Doc'}
                                </p>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              {item.fileData && (
                                <button 
                                  onClick={() => viewPdf(item.fileData)}
                                  className="w-9 h-9 bg-white hover:bg-emerald hover:text-white rounded-lg border border-gray-150 flex items-center justify-center text-slate/50 shadow-sm transition-all duration-300 cursor-pointer"
                                  title="View PDF Document"
                                >
                                  <Eye size={14} />
                                </button>
                              )}
                              {item.fileData && (
                                <a 
                                  href={item.fileData}
                                  download={item.fileName || `${item.title}.pdf`}
                                  className="w-9 h-9 bg-white hover:bg-emerald hover:text-white rounded-lg border border-gray-150 flex items-center justify-center text-slate/50 shadow-sm transition-all duration-300 cursor-pointer"
                                  title="Download PDF File"
                                >
                                  <Download size={14} />
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Downloads;
